import {
  EXERCISE_RELATIONS,
  type AdminExerciseDetail,
  type AdminExerciseEdgeView,
  type ExerciseRelationName,
} from '@steado/admin-contract/exercises';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { BadgeCell } from '@/components/table/cells';
import type { ToneName } from '@/components/table/tone';
import { Empty, Panel } from '@/components/panel/panels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/auth-context';
import { humanise } from '@/lib/format';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';
import { Confirm } from '@/screens/catalog-history/HistoryTab';
import {
  addExerciseEdge,
  exercisesQueryOptions,
  removeExerciseEdge,
} from '@/screens/exercises/exercises-query';

interface AlternativesTabProps {
  readonly exercise: AdminExerciseDetail;
  readonly onMutated: (exercise: AdminExerciseDetail) => void;
}

const RELATION_TONE: Readonly<Record<AdminExerciseEdgeView['relation'], ToneName>> = {
  progression: 'ok',
  regression: 'info',
  lower_skill: 'mute',
  lower_impact: 'mute',
  equipment_alternative: 'purple',
  time_saving: 'warn',
};

/**
 * `direction: 'from'` means this exercise IS `relation` to the target — e.g.
 * this row's `progression` is the target. `'to'` is the inverse: the target
 * is `relation` to this row. Both are shown as one arrow so a curator reads
 * which exercise the relation actually describes without holding the
 * direction word in their head.
 */
function arrow(edge: AdminExerciseEdgeView, ownName: string): string {
  return edge.direction === 'from'
    ? `${ownName} → ${edge.exercise.name}`
    : `${edge.exercise.name} → ${ownName}`;
}

function difficultyLabel(delta: number): string {
  if (delta > 0) return `harder by ${String(delta)}`;
  if (delta < 0) return `easier by ${String(Math.abs(delta))}`;
  return 'same difficulty';
}

/**
 * Curated substitutes, in both directions, added and removed here or pushed
 * with `catalog:push`; every change is in History, where it can be reverted.
 * An empty list is "not reviewed yet", never "no good alternative exists":
 * the table starts empty and every reader, including this one, treats it
 * that way (see `exercise-alternatives.ts` on the consumer side).
 */
export function AlternativesTab({ exercise, onMutated }: AlternativesTabProps) {
  const { api, can } = useAuth();
  const [removing, setRemoving] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (edgeId: string) => removeExerciseEdge(api, exercise.id, edgeId),
    onSuccess: (updated) => {
      setRemoving(null);
      onMutated(updated);
    },
    retry: false,
  });

  return (
    <div className="flex flex-col gap-3.5">
      <Panel
        title="Alternatives"
        description="Curated substitutes, in both directions. Every change is in History and can be reverted."
        bleed
      >
        {exercise.edges.length === 0 ? (
          <div className="border-t border-border">
            <Empty
              title="Not reviewed yet"
              body="No curated alternatives exist for this exercise. That is a statement about review status, not about this movement: nobody has judged it against another one."
            />
          </div>
        ) : (
          <ul>
            {exercise.edges.map((edge) => (
              <li key={edge.id} className="border-t border-border">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-[18px] py-2.5">
                  <BadgeCell label={humanise(edge.relation)} tone={RELATION_TONE[edge.relation]} />
                  <span className="text-sm font-medium text-foreground">{arrow(edge, exercise.name)}</span>
                  <span className="ml-auto flex shrink-0 items-center gap-2">
                    <span className="font-mono text-[13px] text-muted-foreground">
                      {String(Math.round(edge.stimulusMatch * 100))}% match
                    </span>
                    <Button
                      variant="outline"
                      size="xs"
                      disabled={!can.mutate || remove.isPending}
                      title={can.mutate ? undefined : 'Changing the library requires the admin role.'}
                      onClick={() => {
                        setRemoving(edge.id);
                        remove.reset();
                      }}
                    >
                      Remove
                    </Button>
                  </span>
                  <span className="basis-full text-[13px] text-muted-foreground">
                    {difficultyLabel(edge.difficultyDelta)}
                    {edge.context === null ? '' : ` · ${edge.context}`} · reviewed by {edge.reviewer}
                    {edge.evidence === null ? '' : ` · ${edge.evidence}`}
                  </span>
                </div>
                {removing === edge.id ? (
                  <Confirm
                    label="Remove alternative"
                    text={`${arrow(edge, exercise.name)} stops being offered. Saved as a new change in History, where it can be reverted.`}
                    error={remove.isError ? remove.error.message : null}
                    isPending={remove.isPending}
                    onCancel={() => {
                      setRemoving(null);
                    }}
                    onConfirm={() => {
                      remove.mutate(edge.id);
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {can.mutate ? <AddAlternative exercise={exercise} onMutated={onMutated} /> : null}
    </div>
  );
}

function AddAlternative({ exercise, onMutated }: AlternativesTabProps) {
  const { api } = useAuth();
  const [term, setTerm] = useState('');
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);
  const [relation, setRelation] = useState<ExerciseRelationName | ''>('');
  const [match, setMatch] = useState('80');
  const [delta, setDelta] = useState('-1');
  const [context, setContext] = useState('');
  const [evidence, setEvidence] = useState('');
  const debounced = useDebounced(term.trim(), 260);
  const ids = { search: useId(), match: useId(), delta: useId(), context: useId(), evidence: useId() };

  const candidates = useQuery({
    ...exercisesQueryOptions(api, { search: debounced }),
    enabled: target === null && debounced.length >= 2,
  });

  const add = useMutation({
    mutationFn: () =>
      addExerciseEdge(api, exercise.id, {
        toExerciseId: target?.id ?? '',
        relation: relation === '' ? 'regression' : relation,
        stimulusMatch: Number(match) / 100,
        difficultyDelta: Number(delta),
        context: context.trim() === '' ? null : context.trim(),
        evidence: evidence.trim() === '' ? null : evidence.trim(),
      }),
    onSuccess: (updated) => {
      setTarget(null);
      setTerm('');
      setContext('');
      setEvidence('');
      onMutated(updated);
    },
    retry: false,
  });

  const matchValue = Number(match);
  const matchError =
    match.trim() === '' || !Number.isFinite(matchValue) || matchValue < 0 || matchValue > 100;

  return (
    <Panel
      title="Add an alternative"
      description={`What another exercise is to ${exercise.name}. Your name is recorded as its reviewer.`}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor={ids.search} className="text-xs font-medium text-muted-foreground">
            Exercise
          </Label>
          {target === null ? (
            <>
              <Input
                id={ids.search}
                type="search"
                value={term}
                placeholder="Search the library"
                onChange={(event) => {
                  setTerm(event.target.value);
                }}
              />
              <ul className="flex flex-col">
                {(candidates.data?.items ?? [])
                  .filter((candidate) => candidate.id !== exercise.id)
                  .slice(0, 6)
                  .map((candidate) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setTarget({ id: candidate.id, name: candidate.name });
                        }}
                        className="w-full rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent"
                      >
                        {candidate.name}
                        <span className="ml-2 font-mono text-[12px] text-muted-foreground">
                          {candidate.slug}
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-card px-2.5 text-sm">
              <span className="min-w-0 flex-1 truncate">{target.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTarget(null);
                }}
              >
                Change
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">It is this exercise’s</span>
          <Select
            value={relation}
            onValueChange={(next) => {
              setRelation(next as ExerciseRelationName);
            }}
          >
            <SelectTrigger aria-label="Relation" className="w-full">
              <SelectValue placeholder="Pick one" />
            </SelectTrigger>
            <SelectContent>
              {EXERCISE_RELATIONS.map((value) => (
                <SelectItem key={value} value={value}>
                  {humanise(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.match} className="text-xs font-medium text-muted-foreground">
              Trains the same, %
            </Label>
            <Input
              id={ids.match}
              inputMode="numeric"
              value={match}
              aria-invalid={matchError}
              onChange={(event) => {
                setMatch(event.target.value);
              }}
              className="font-mono"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.delta} className="text-xs font-medium text-muted-foreground">
              Harder by
            </Label>
            <Select value={delta} onValueChange={setDelta}>
              <SelectTrigger id={ids.delta} aria-label="Harder by" className="w-full font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map((value) => (
                  <SelectItem key={value} value={String(value)}>
                    {value > 0 ? `+${String(value)}` : String(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.context} className="text-xs font-medium text-muted-foreground">
            When it applies (optional)
          </Label>
          <Input
            id={ids.context}
            value={context}
            maxLength={280}
            placeholder="Such as knee flexion limited"
            onChange={(event) => {
              setContext(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.evidence} className="text-xs font-medium text-muted-foreground">
            Why (optional)
          </Label>
          <Input
            id={ids.evidence}
            value={evidence}
            maxLength={500}
            placeholder="A study or a coaching cue"
            onChange={(event) => {
              setEvidence(event.target.value);
            }}
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {add.isError ? (
          <span role="alert" className="min-w-0 flex-1 text-[12.5px] font-medium text-err-fg">
            {add.error.message}
          </span>
        ) : (
          <span className="min-w-0 flex-1 text-[12.5px] text-muted-foreground">
            Saved as a change in History.
          </span>
        )}
        <Button
          disabled={target === null || relation === '' || matchError || add.isPending}
          className={cn(add.isPending && 'opacity-70')}
          onClick={() => {
            add.mutate();
          }}
        >
          {add.isPending ? 'Adding…' : 'Add alternative'}
        </Button>
      </div>
    </Panel>
  );
}
